import * as React from 'react';
import { useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import { useLazyTermItemsQuery } from '../../../../../graphql/graphql';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import useTranslation from '../../../../../Hooks/useTranslation';
import { useEffectAsync } from '../../../../../helpers';

interface Props {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	termType: string;
}

const TermItemAsyncSelect: React.FC<Props> = ({
	value,
	onChange,
	termType,
}) => {
	const [searchTermItems] = useLazyTermItemsQuery();
	const { t } = useTranslation();
	const [innerValue, setInnerValue] = useState<any>();
	const [selectInputValue, setSelectInputValue] = useState<string>('');
	const [options, setOptions] = useState<SelectValue[]>([]);
	const [page, setPage] = useState(1);

	const handleChange = (val: SelectValue) => {
		onChange?.(val);
	};

	const handleSearchItem = async (
		searchInput: string,
		page?: number,
	): Promise<SelectValue[]> => {
		const { termItems } = await searchTermItems({
			fullText: searchInput,
			termType: termType,
			pagination: {
				page: page || 1,
			},
		}).unwrap();

		const items: SelectValue[] = [];

		for (const termItem of termItems?.data || []) {
			if (termItem) {
				items.push({
					value: termItem.id,
					label: termItem.name ?? termItem.id,
				});
			}
		}

		return items;
	};

	const handleLoadOptionsBySearch = async (inputValue: string) => {
		setPage(1);
		const newOptions = await handleSearchItem(inputValue, 1);
		setOptions(newOptions);
	};

	const handleLoadMoreOptions = async () => {
		setPage(prev => prev + 1);
		const newOptions = await handleSearchItem(selectInputValue, page + 1);
		setOptions(prevOptions => [...prevOptions, ...newOptions]);
	};

	useEffectAsync(async () => {
		await handleLoadOptionsBySearch(selectInputValue);
	}, [selectInputValue]);

	useEffect(() => {
		setInnerValue(value);
	}, [value]);

	return (
		<ReactSelect
			classNamePrefix={'reactSearchSelect'}
			options={options}
			value={innerValue}
			onChange={handleChange}
			menuPortalTarget={document.body}
			isSearchable={true}
			onMenuScrollToBottom={handleLoadMoreOptions}
			onInputChange={setSelectInputValue}
			placeholder={t('base.shared.pick')}
		/>
	);
};

export default TermItemAsyncSelect;
